import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Copy, Check, UserPlus, ChevronRight } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  CommissionReport,
  CommissionReportRep,
  SelfServiceSalesperson,
  SelfServiceSubmissionRow,
} from '../../types/selfService';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { rankOf } from '../../components/self-service/shared/roleRank';
import { publisherStatusBadge } from '../../components/self-service/campaignSharedHelpers';
import { apiClient } from '../../lib/apiClient';

type SubmissionRow = Pick<
  SelfServiceSubmissionRow,
  'id' | 'business_info' | 'contact_email' | 'campaign' | 'amount_cents'
  | 'service_fee_cents' | 'status' | 'created_at' | 'referral_code'
>;

const PublisherReferralDetailPage: React.FC = () => {
  const { slug } = useSingleStationContext();
  const { user } = useAuth();
  const { salespersonId } = useParams<{ salespersonId: string }>();
  // CTV-671: commission data is manager+, same gate as the Referrals index.
  if (rankOf(user?.rawRole) < rankOf('manager')) {
    return <Navigate to={`/admin/${slug}`} replace />;
  }
  if (!salespersonId) return <Navigate to={`/admin/${slug}/referrals`} replace />;
  return <Inner slug={slug} salespersonId={salespersonId} />;
};

const Inner: React.FC<{ slug: string; salespersonId: string }> = ({ slug, salespersonId }) => {
  const [salesperson, setSalesperson] = React.useState<SelfServiceSalesperson | null>(null);
  const [repReport, setRepReport] = React.useState<CommissionReportRep | null>(null);
  const [submissions, setSubmissions] = React.useState<SubmissionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Roster + submissions + ledger-backed commission report in parallel;
        // client-side filter to scope to this rep.
        const [spData, subData, reportData] = await Promise.all([
          apiClient.post<{ rows: SelfServiceSalesperson[] }>('/salespeople/list', { slug }),
          apiClient.post<{ rows: SubmissionRow[] }>('/submissions/list-by-publisher', { slug }),
          apiClient.post<CommissionReport>('/salespeople/commission-report', { slug }),
        ]);
        if (cancelled) return;
        const sp = (spData.rows ?? []).find((r) => r.id === salespersonId) ?? null;
        setSalesperson(sp);
        setRepReport(
          (reportData.reps ?? []).find((r) => r.salesperson_id === salespersonId) ?? null,
        );
        if (sp) {
          const all = subData.rows ?? [];
          setSubmissions(all.filter((s) => (s.referral_code ?? null) === sp.code));
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, salespersonId]);

  const shareUrl = salesperson
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/${slug}?ref=${salesperson.code}`
    : '';

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  // Roll-up stats. "Approved revenue" only counts captured charges since
  // authorized holds may still get rejected and never become real $.
  // (Bonus plays the rep "gave away" used to be back-calculated as a $
  // discount; under the new model, the discount is delivered as extra ad
  // plays at full price, so dollar discount given is always 0. We could
  // eventually surface "bonus plays delivered" once we track per-submission
  // CPM — TODO.)
  const stats = React.useMemo(() => {
    // Status enum migrated: legacy 'captured' → 'approved', 'authorized' → 'payment_authorized'.
    const captured = submissions.filter((s) => s.status === 'approved');
    const capturedRevenue = captured.reduce((acc, s) => acc + s.amount_cents, 0);
    const pendingCount = submissions.filter((s) => s.status === 'payment_authorized').length;
    return {
      total: submissions.length,
      capturedCount: captured.length,
      pendingCount,
      capturedRevenueDollars: capturedRevenue / 100,
    };
  }, [submissions]);

  return (
    <div className="max-w-5xl">
      <Link to={`/admin/${slug}/referrals`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to referrals
      </Link>

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>
        ) : !salesperson ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <UserPlus className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">Salesperson not found</p>
          </div>
        ) : (
          <>
              {/* CTV-759: no scope selector — a salesperson record belongs to
                  one station, so there is nothing to switch to here. */}
            <AdminPageHeader
              eyebrow="Salesperson"
              title={salesperson.name}
              subtitle={
                <span className="flex items-center gap-3 flex-wrap">
                  <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 font-mono text-[12px]">{salesperson.code}</code>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${salesperson.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                    {salesperson.active ? 'Active' : 'Disabled'}
                  </span>
                  {salesperson.default_discount_percent != null && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">
                      Offers +{salesperson.default_discount_percent}% bonus plays
                    </span>
                  )}
                  {salesperson.email && <span className="text-slate-600">{salesperson.email}</span>}
                </span>
              }
              scopeMode="none"
              currentSlug={slug}
              actions={
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md whitespace-nowrap"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy Share Link'}
                </button>
              }
            />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Stat label="Total submissions" value={stats.total.toString()} />
              <Stat label="Approved" value={stats.capturedCount.toString()} />
              <Stat label="Pending review" value={stats.pendingCount.toString()} />
              <Stat
                label="Approved revenue"
                value={`$${stats.capturedRevenueDollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              />
            </div>

            {/* CTV-671: ledger-backed figures — collected money net of rev
                share and Stripe fees, and the rep's commission on it. */}
            {repReport && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <Stat label="Net revenue (collected)" value={formatDollars(repReport.total_net_revenue_cents)} />
                <Stat
                  label="Commission earned"
                  value={
                    repReport.total_net_revenue_cents > 0 || repReport.effective_commission_percent != null
                      ? formatDollars(repReport.total_commission_cents)
                      : '—'
                  }
                />
                <Stat
                  label="Commission rate"
                  value={
                    repReport.effective_commission_percent != null
                      ? `${repReport.effective_commission_percent}%${repReport.commission_percent == null ? ' (default)' : ''}`
                      : 'Not set'
                  }
                />
              </div>
            )}

            {repReport && repReport.monthly.length > 0 && (
              <MonthlyRevenueChart rep={repReport} />
            )}

            <div className="mb-3">
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Attributed campaigns</h2>
            </div>

            {submissions.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <p className="text-slate-700 font-medium">No campaigns attributed yet</p>
                <p className="text-sm text-slate-500 mt-1">When an advertiser uses this code, their campaign will show up here.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {submissions.map((s) => {
                  const total = (s.amount_cents / 100).toFixed(2);
                  // CTV-671: the % locked onto this campaign at submission.
                  // Rejected / fully-refunded campaigns have no entry (no
                  // chip); a canceled monthly campaign keeps its entry for
                  // the installments the publisher kept.
                  const attribution = repReport?.campaigns.find((c) => c.submission_id === s.id);
                  return (
                    <li key={s.id}>
                      <Link
                        to={`/admin/${slug}/submissions/${s.id}`}
                        className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-400 hover:shadow-sm transition-all"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-base font-semibold text-slate-900 truncate">
                              {s.business_info?.businessName || 'Advertiser'}
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${publisherStatusBadge(s.status).className}`}>
                              {publisherStatusBadge(s.status).label}
                            </span>
                            {attribution && attribution.commission_percent != null ? (
                              <span
                                className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
                                title="Locked when this campaign was submitted. Later changes to the rep's % don't affect it."
                              >
                                {attribution.commission_percent}% commission
                              </span>
                            ) : attribution ? (
                              // CTV-673: pure lock-at-submission — no
                              // snapshot means no commission, ever.
                              <span
                                className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-400 border border-slate-200"
                                title="No commission rate existed when this campaign was submitted, so it earns no commission."
                              >
                                no commission rate
                              </span>
                            ) : null}
                          </div>
                          <div className="text-xs text-slate-500">
                            ${total} · {new Date(s.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                            {attribution && attribution.commission_cents > 0 && (
                              <span className="text-emerald-700">
                                {' '}· ${(attribution.commission_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} commission earned
                              </span>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
        </>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</div>
    <div className="text-2xl font-bold text-slate-900">{value}</div>
  </div>
);

const formatDollars = (cents: number) =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatMonth = (yyyyMm: string) => {
  const d = new Date(`${yyyyMm}-01T00:00:00`);
  // Full year: "Jul 26" (2-digit) reads like a day of the month.
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// CTV-671: money collected per month for this rep's campaigns (net revenue,
// with the commission share alongside). A monthly campaign lands a bar in
// every month an installment is charged, which is exactly the story a rep
// should see: their sale keeps paying out.
const MonthlyRevenueChart: React.FC<{ rep: CommissionReportRep }> = ({ rep }) => {
  const data = rep.monthly.map((m) => ({
    month: m.month,
    netRevenueDollars: m.net_revenue_cents / 100,
    commissionDollars: m.commission_cents / 100,
    campaigns: m.campaign_count,
  }));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Monthly net revenue &amp; commission</div>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 12, fill: '#64748b' }} stroke="#94a3b8" />
            <YAxis
              tickFormatter={(v: number) => `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
              tick={{ fontSize: 12, fill: '#64748b' }}
              stroke="#94a3b8"
            />
            <Tooltip
              formatter={(value: number, name: string) => [formatDollars(Math.round(value * 100)), name]}
              labelFormatter={(label: string, payload) => {
                const row = payload?.[0]?.payload as { campaigns?: number } | undefined;
                const base = formatMonth(label);
                return row?.campaigns != null
                  ? `${base} · ${row.campaigns} campaign${row.campaigns === 1 ? '' : 's'} paying`
                  : base;
              }}
            />
            <Bar dataKey="netRevenueDollars" name="Net revenue" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={48} />
            <Bar dataKey="commissionDollars" name="Commission" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex items-center gap-4 text-[12px] text-slate-600">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" /> Net revenue</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Commission</span>
      </div>
    </div>
  );
};

export default PublisherReferralDetailPage;
